import { Column, Entity, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { BaseEntity } from './base.entity';
import { NamedLocationEntity } from './named-location.entity';
import { SalesRouteClientEntity } from './sales-route-client.entity';

/**
 * A salesperson's recurring **sales route** — a named, scheduled plan for
 * visiting a set of clients (e.g. "downtown accounts, every Tuesday"). It is
 * the sales-side analogue of the delivery `RouteEntity`, but deliberately
 * leaner: a sales route is a *planning guide*, not a dispatch blueprint
 * (PD-13776).
 *
 * Key differences from a delivery route:
 * - **No trips or stops are ever generated.** A sales route is expanded on the
 *   fly into a per-day "who am I due to see today" list from `recurrenceRule`
 *   (narrowed by each client's compatible child cadence — see
 *   `SalesRouteClientEntity.recurrenceRule`). There is no materialised
 *   execution record, and therefore no concept of a "missed" stop: a day that
 *   passes is simply gone, never reconstructed. That is what makes the daily
 *   list self-cleaning.
 * - **No warehouse / vehicle / driver / day-night classification** — none of
 *   the delivery dispatch machinery applies.
 * - **Ownership is the salesperson.** The route is owned via BaseEntity's
 *   `_owner_userId` (auto-stamped to the creating user by
 *   AuditFieldsSubscriber). A rep manages only their own routes
 *   (`sales_routes:*:self`); leaders with `sales_routes:*:any` see and
 *   maintain every rep's routes, labelled by owner.
 *
 * The only thing genuinely shared with the delivery side is the pure RRULE /
 * child-cadence math in `libs/core` (entity-agnostic), reused verbatim.
 */
@Entity('sales_route')
export class SalesRouteEntity extends BaseEntity {
	@Column({ name: 'name', type: 'varchar', length: 255 })
	public name!: string;

	@Column({ name: 'description', type: 'text', nullable: true })
	public description?: string | null;

	/**
	 * The route's recurrence as an iCal RRULE string (e.g.
	 * `FREQ=WEEKLY;INTERVAL=1;BYDAY=TU`). NULL = no recurrence (an ad-hoc route
	 * that never auto-surfaces on the daily list). Expanded by the shared
	 * `libs/core` rrule utilities — the same ones the delivery side uses.
	 */
	@Column({
		name: 'recurrenceRule',
		type: 'varchar',
		length: 500,
		nullable: true,
	})
	public recurrenceRule?: string | null;

	/**
	 * Date this route starts being eligible to surface clients. Combined with
	 * `expirationDate` and `recurrenceRule`, the route is "due" on a date D iff
	 * `effectiveDate <= D <= (expirationDate ?? +∞)` AND the RRULE fires on D.
	 * Mirrors the delivery route's effective/expiration windowing.
	 */
	@Column({ name: 'effectiveDate', type: 'date', nullable: true })
	public effectiveDate?: Date | null;

	/**
	 * Last date this route is eligible. NULL = never expires (the common case);
	 * set a past date to retire a route without deleting it.
	 */
	@Column({ name: 'expirationDate', type: 'date', nullable: true })
	public expirationDate?: Date | null;

	/** Suggested time of day the rep starts this route (a planning aid only). */
	@Column({ name: 'defaultStartTime', type: 'time', nullable: true })
	public defaultStartTime?: string | null;

	@Column({ name: 'isActive', type: 'boolean', default: true })
	public isActive!: boolean;

	/**
	 * PD-15920 — default start / end points for trips created from this route
	 * (decision 8). Nullable. A trip copies these at creation and may override
	 * per day.
	 */
	@Column({
		name: 'fk_named_location__id_start',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public namedLocationIdStart?: number | null;

	@Column({
		name: 'fk_named_location__id_end',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public namedLocationIdEnd?: number | null;

	@ManyToOne(() => NamedLocationEntity, { nullable: true })
	@JoinColumn({ name: 'fk_named_location__id_start' })
	public fkNamedLocationStart?: NamedLocationEntity | null;

	@ManyToOne(() => NamedLocationEntity, { nullable: true })
	@JoinColumn({ name: 'fk_named_location__id_end' })
	public fkNamedLocationEnd?: NamedLocationEntity | null;

	@OneToMany(() => SalesRouteClientEntity, (src) => src.fkSalesRoute)
	public salesRouteClients?: Array<SalesRouteClientEntity>;
}
